import { Time } from 'lightweight-charts'
import { Point } from '../../../components/chartV2/drawingTools/drawing.interface'
import { ChartPatternTargetStatus } from './chartPatternTypes'

export type Pivot = {
    index: number
    price: number
}

export type TypedPivot = Pivot & {
    type: 'high' | 'low'
}

export type AlternatingSwing = Pivot & {
    kind: 'h' | 'l'
}

export type BasePatternDetectOptions = {
    trendHeight?: number
    inProgress?: boolean
    priceTargetsFilter?: string
    tolerancePct?: number
    maxPatterns?: number
}

export const toPatternPoint = (id: string, timestampMs: number, price: number): Point => ({
    id,
    x: Math.floor(timestampMs / 1000) as Time,
    y: price,
})

/** TradingView auto chart patterns use 5/5 pivots */
export const CHART_PATTERN_PIVOT_LENGTH = 5

export function resolveChartPatternPivotLength(_trendHeight?: number): number {
    return CHART_PATTERN_PIVOT_LENGTH
}

/** @deprecated Use resolveChartPatternPivotLength for TV auto chart patterns */
export function resolvePivotLength(trendHeight: number): number {
    return Math.max(2, Math.min(15, Math.floor(trendHeight / 10) || 5))
}

/** TradingView auto chart patterns scan only the most recent 600 bars */
export const PATTERN_DETECTION_LOOKBACK_BARS = 600

export function resolveLookbackBars(_trendHeight?: number): number {
    return PATTERN_DETECTION_LOOKBACK_BARS
}

export function findPivotHighs(high: number[], left: number, right: number): Pivot[] {
    const pivots: Pivot[] = []
    for (let i = left; i < high.length - right; i++) {
        const value = high[i]
        if (!Number.isFinite(value)) continue
        let isPivot = true
        for (let j = i - left; j <= i + right; j++) {
            if (j === i) continue
            if (high[j] >= value) {
                isPivot = false
                break
            }
        }
        if (isPivot) pivots.push({ index: i, price: value })
    }
    return pivots
}

/** Combined pivot highs and lows sorted by bar index */
export function findPivots(
    highs: number[],
    lows: number[],
    left = CHART_PATTERN_PIVOT_LENGTH,
    right = CHART_PATTERN_PIVOT_LENGTH,
): TypedPivot[] {
    const pivots: TypedPivot[] = []

    for (let i = left; i < highs.length - right; i++) {
        const highValue = highs[i]
        const lowValue = lows[i]
        if (!Number.isFinite(highValue) || !Number.isFinite(lowValue)) continue

        let highPivot = true
        let lowPivot = true

        for (let j = i - left; j <= i + right; j++) {
            if (j === i) continue
            if (highs[j] >= highValue) highPivot = false
            if (lows[j] <= lowValue) lowPivot = false
        }

        if (highPivot) {
            pivots.push({ index: i, price: highValue, type: 'high' })
        }
        if (lowPivot) {
            pivots.push({ index: i, price: lowValue, type: 'low' })
        }
    }

    return pivots.sort((a, b) => a.index - b.index)
}

/** Merge pivot highs/lows into an alternating H-L-H-L swing sequence */
export function buildAlternatingSwings(high: number[], low: number[], pivotLength: number): AlternatingSwing[] {
    const highs = findPivotHighs(high, pivotLength, pivotLength).map((p) => ({ ...p, kind: 'h' as const }))
    const lows = findPivotLows(low, pivotLength, pivotLength).map((p) => ({ ...p, kind: 'l' as const }))
    const merged = [...highs, ...lows].sort((a, b) => a.index - b.index)

    const filtered: AlternatingSwing[] = []
    merged.forEach((swing) => {
        const last = filtered[filtered.length - 1]
        if (!last) {
            filtered.push(swing)
            return
        }
        if (last.kind === swing.kind) {
            if (swing.kind === 'h' && swing.price >= last.price) {
                filtered[filtered.length - 1] = swing
            } else if (swing.kind === 'l' && swing.price <= last.price) {
                filtered[filtered.length - 1] = swing
            }
            return
        }
        filtered.push(swing)
    })
    return filtered
}

export function findPivotLows(low: number[], left: number, right: number): Pivot[] {
    const pivots: Pivot[] = []
    for (let i = left; i < low.length - right; i++) {
        const value = low[i]
        if (!Number.isFinite(value)) continue
        let isPivot = true
        for (let j = i - left; j <= i + right; j++) {
            if (j === i) continue
            if (low[j] <= value) {
                isPivot = false
                break
            }
        }
        if (isPivot) pivots.push({ index: i, price: value })
    }
    return pivots
}

export function findPivotLowBetween(lows: Pivot[], startIdx: number, endIdx: number): Pivot | null {
    const candidates = lows.filter((p) => p.index > startIdx && p.index < endIdx)
    if (!candidates.length) return null
    return candidates.reduce((best, current) => (current.price < best.price ? current : best))
}

export function findPivotHighBetween(highs: Pivot[], startIdx: number, endIdx: number): Pivot | null {
    const candidates = highs.filter((p) => p.index > startIdx && p.index < endIdx)
    if (!candidates.length) return null
    return candidates.reduce((best, current) => (current.price > best.price ? current : best))
}

export function pricesAreSimilar(a: number, b: number, tolerancePct: number): boolean {
    const avg = (a + b) / 2
    if (!Number.isFinite(avg) || avg === 0) return false
    return (Math.abs(a - b) / avg) * 100 <= tolerancePct
}

export function linePriceAt(
    timestamps: number[],
    left: Pivot,
    right: Pivot,
    targetIdx: number
): number {
    const tLeft = timestamps[left.index]
    const tRight = timestamps[right.index]
    const tTarget = timestamps[targetIdx]
    if (tRight === tLeft) return left.price
    const slope = (right.price - left.price) / (tRight - tLeft)
    return left.price + slope * (tTarget - tLeft)
}

export function pivotSlope(left: Pivot, right: Pivot): number {
    const span = right.index - left.index
    if (span <= 0) return 0
    return (right.price - left.price) / span
}

export function slopesAreSymmetric(
    highSlope: number,
    lowSlope: number,
    priceScale: number,
    tolerancePct = 25,
): boolean {
    if (highSlope >= 0 || lowSlope <= 0) return false
    const absHigh = Math.abs(highSlope * priceScale)
    const absLow = Math.abs(lowSlope * priceScale)
    const avg = (absHigh + absLow) / 2
    if (avg === 0) return false
    return (Math.abs(absHigh - absLow) / avg) * 100 <= tolerancePct
}

export function slopesAreParallel(slopeA: number, slopeB: number, priceScale: number, tolerancePct = 25): boolean {
    const normalizedA = slopeA * priceScale
    const normalizedB = slopeB * priceScale
    const avg = (Math.abs(normalizedA) + Math.abs(normalizedB)) / 2
    if (avg === 0) return true
    return (Math.abs(normalizedA - normalizedB) / avg) * 100 <= tolerancePct
}

export function slopeIsNearHorizontal(slope: number, priceScale: number, maxPct = 0.35): boolean {
    return Math.abs(slope * priceScale) * 100 <= maxPct
}

export function shouldIncludePriceTarget(priceTargetsFilter?: string): boolean {
    if (!priceTargetsFilter || priceTargetsFilter === 'All') return true
    if (priceTargetsFilter === 'None' || priceTargetsFilter === 'Disabled') return false
    return true
}

/** TradingView Price Targets visibility filter applied at render time using pattern status */
export function shouldDrawPriceTarget(
    priceTargetsFilter: string | undefined,
    status: ChartPatternTargetStatus,
): boolean {
    if (!shouldIncludePriceTarget(priceTargetsFilter)) return false
    const filter = priceTargetsFilter ?? 'All'
    if (filter === 'All' || filter === 'Enabled') return true
    if (filter === 'Only Awaiting') {
        return status === 'Awaiting' || status === 'In progress'
    }
    if (filter === 'Awaiting and Reached') {
        return status === 'Awaiting' || status === 'In progress' || status === 'Reached'
    }
    return true
}

export function sortPatternsByEndTime<T extends { points: Point[] }>(patterns: T[]): T[] {
    return [...patterns].sort((a, b) => {
        const aTime = (a.points[a.points.length - 1]?.x as number) ?? 0
        const bTime = (b.points[b.points.length - 1]?.x as number) ?? 0
        return bTime - aTime
    })
}

export function sliceRecentOpenHighLowClose(
    open: number[],
    high: number[],
    low: number[],
    close: number[],
    timestamps: number[],
    lookbackBars = PATTERN_DETECTION_LOOKBACK_BARS,
) {
    const start = Math.max(0, high.length - lookbackBars)
    return {
        open: open.slice(start),
        high: high.slice(start),
        low: low.slice(start),
        close: close.slice(start),
        timestamps: timestamps.slice(start),
    }
}

export function sliceRecentOhlc(
    high: number[],
    low: number[],
    timestamps: number[],
    lookbackBars: number
): { high: number[]; low: number[]; timestamps: number[] } {
    if (high.length <= lookbackBars) {
        return { high, low, timestamps }
    }
    const start = high.length - lookbackBars
    return {
        high: high.slice(start),
        low: low.slice(start),
        timestamps: timestamps.slice(start),
    }
}

export function pickLatestPatterns<T extends { points: Point[]; inProgress?: boolean }>(
    patterns: T[],
    maxPatterns: number,
    preferInProgress = false
): T[] {
    const sorted = sortPatternsByEndTime(patterns)
    if (!sorted.length) return []
    if (maxPatterns <= 0) return sorted

    if (preferInProgress) {
        const inProgress = sorted.find((pattern) => pattern.inProgress)
        if (inProgress) return [inProgress]
    }

    return sorted.slice(0, maxPatterns)
}

export function limitPatterns<T extends { points: Point[]; inProgress?: boolean }>(
    patterns: T[],
    maxPatterns = 1,
    preferInProgress = false
): T[] {
    if (maxPatterns === 0) return patterns
    return pickLatestPatterns(patterns, maxPatterns, preferInProgress)
}
